import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { UserFormModal } from './components/UserFormModal';
import { UsersTable } from './components/UsersTable';
import { useUsersPageController } from './useUsersPageController';
import classes from './users-page.module.scss';

// Architectural summary:
// Super-admin Users management screen (Hebrew, RTL).
// Touches:
//   - users.service.ts (create / update / deactivate / activate)
//   - AdminEventsProvider (live `users` + active-tenant chip snapshots — page does NOT open its own SSE)
//   - AuthenticatedRoutes /users (gated by SuperAdminOnly)
//   - users-page.module.scss (theme tokens only)
//   - Mantine MantineProvider must wrap this tree
// Create + edit are unified into a single Mantine Modal: the create button
// opens it in "create" mode (blank form), the row "ערוך" button opens it in
// "edit" mode (prefilled, username+email locked, password optional).
export const UsersPage: React.FC = () => {
  const {
    users,
    isLoadingUsers,
    tenants,
    selectedTenantIds,
    form,
    isCreating,
    isSaving,
    isModalOpen,
    expandedUserId,
    displayedError,
    usersCountLabel,
    isEditing,
    modalTitle,
    submitLabel,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSubmitUser,
    deactivateUser,
    activateUser,
    onTenantToggle,
    onChangeForm,
    onToggleExpanded,
  } = useUsersPageController();

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.titleBlock}>
            <h2 className={classes.title}>ניהול משתמשים</h2>
            <p className={classes.subtitle}>{usersCountLabel}</p>
          </div>
          <button type="button" className={classes.createButton} onClick={openCreateModal} data-testid="users-create">
            <span>צור משתמש</span>
            <FontAwesomeIcon icon={faPlus as IconProp} className={classes.plusIcon} aria-hidden="true" />
          </button>
        </div>

        {displayedError && <p className={classes.error}>{displayedError}</p>}
        <UsersTable
          users={users}
          isLoadingUsers={isLoadingUsers}
          expandedUserId={expandedUserId}
          onToggleExpanded={onToggleExpanded}
          onEditUser={openEditModal}
          onDeactivateUser={(user) => {
            void deactivateUser(user);
          }}
          onActivateUser={(user) => {
            void activateUser(user);
          }}
        />
      </div>

      <UserFormModal
        isOpen={isModalOpen}
        isEditing={isEditing}
        modalTitle={modalTitle}
        submitLabel={submitLabel}
        form={form}
        tenants={tenants}
        selectedTenantIds={selectedTenantIds}
        isSubmitting={isCreating || isSaving}
        onClose={closeModal}
        onSubmit={handleSubmitUser}
        onChangeForm={onChangeForm}
        onToggleTenant={onTenantToggle}
      />
    </div>
  );
};
